import { graphql } from "gatsby";
import React, { useContext, useState, useEffect, useRef } from "react";
import { LayoutContainer } from "../containers/layout";
import PageHeadMeta from "../components/global/page-head-meta";
import {
  ChannelsButtons,
  Divider,
  GetStarted,
  HubspotWebform,
  ResourcesSuggestions,
  SectionWrapper,
} from "../components/sections";
import { ExternalVideo } from "../components/serializers-video";
import { SiteContext } from "../components/global/site-context";
import { BackToZone } from "../components/back-to-zone";
import getYouTubeID from "get-youtube-id";
import { cn, mapEdgesToNodes } from "../lib/helpers";
import PortableText from "../components/portableText";
import { CardGrid } from "../components/card/card-grid";
import { pageDocPath } from "../../helpers";
import { Button } from "../components/button";
import { ArrowLeft } from "../components/icon/arrow-left";
import { videoMetaData } from "./video-bulk";

import * as styles from "./video.module.css";
import * as postStyles from "../components/post-page.module.css";
import { HUBSPOT_ASSET_FORM_ID } from "./asset";

export const query = graphql`
  query VideoTemplateQuery($id: String!, $series: [String!]) {
    doc: sanityVideo(id: { eq: $id }) {
      _type
      _id
      status
      _updatedAt
      _createdAt
      title
      slug {
        current
      }
      mainImage {
        ...SanityImage
        alt
      }
      mainVideo {
        ...externalVideo
      }
      mainAudio {
        asset {
          url
        }
      }
      gatedVideo
      _rawBody(resolveReferences: { maxDepth: 12 })
      duration
      series {
        _type
        customPath
        title
        titlePublic
        slug {
          current
        }
      }
    }
    seriesVideos: allSanityVideo(
      limit: 4
      sort: { fields: [_createdAt], order: DESC }
      filter: {
        slug: { current: { ne: null } }
        id: { ne: $id }
        series: { elemMatch: { _id: { in: $series } } }
        status: { in: ["published", "hidden"] }
      }
    ) {
      edges {
        node {
          ...cardNodeVideo
        }
      }
    }
    seriesVideosBulk: allGoogleSpreadsheetWwWebsiteContentVideos(
      limit: 4
      sort: { fields: [publishDate], order: DESC }
      filter: {
        videoId: { ne: $id }
        status: { eq: "published" }
        series: { in: $series }
      }
    ) {
      edges {
        node {
          videoId
          slug
          status
          series
          title
          playlist
          community
          devRel
          gtm
          publishDate
        }
      }
    }
    seriesPosts: allSanityPost(
      limit: 3
      sort: { fields: [_createdAt], order: DESC }
      filter: {
        slug: { current: { ne: null } }
        categories: { elemMatch: { _id: { in: $series } } }
        status: { eq: "published" }
      }
    ) {
      edges {
        node {
          ...cardNode
        }
      }
    }
  }
`;

const VideoPageTemplate = (props) => {
  const { data = {} } = props;
  const { title } = data.doc || {};

  return (
    <LayoutContainer doc={data.doc}>
      <PageHeadMeta title={`${title} [video]`} doc={data.doc} />
      <VideoPage
        {...data.doc}
        seriesVideos={mapEdgesToNodes(data.seriesVideos).concat(
          mapEdgesToNodes(data.seriesVideosBulk).map(videoMetaData)
        )}
      />
    </LayoutContainer>
  );
};

export default VideoPageTemplate;

const VideoPage = (props) => {
  const { title, mainVideo, _rawBody, seriesVideos, series, gatedVideo, slug } = props;
  const site = useContext(SiteContext);

  const [videoUnlocked, setVideoUnlocked] = useState(!gatedVideo);
  const videoContainerRef = useRef(null);

  const videoId = mainVideo?.url ? getYouTubeID(mainVideo.url) : null;
  const seriesPath = series.length > 0 ? pageDocPath(series[0]) : null;
  const seriesTitle =
    series.length > 0 ? series[0].titlePublic || series[0].title : null;

  const handleFormSubmit = () => {
    setVideoUnlocked(true);

    // Analytics tracking
    if (gatedVideo && slug) {
      site.metrics.logClick("video-gated-unlock", slug.current);
    }
  };

  // Auto-play video after unlock
  useEffect(() => {
    if (videoUnlocked && gatedVideo && videoContainerRef.current) {
      // Delay to allow ExternalVideo component to mount
      const timer = setTimeout(() => {
        // Try to find and play Vimeo video (preferred)
        const vimeoIframe = videoContainerRef.current.querySelector('iframe[src*="vimeo.com"]');
        if (vimeoIframe && vimeoIframe.contentWindow) {
          vimeoIframe.contentWindow.postMessage('{"method":"play"}', '*');
        }

        // Try to find and play YouTube video
        const youtubeIframe = videoContainerRef.current.querySelector('iframe[src*="youtube.com"]');
        if (youtubeIframe && youtubeIframe.contentWindow) {
          youtubeIframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
        }

        // Try to find and play self-hosted video
        const videoElement = videoContainerRef.current.querySelector('video');
        if (videoElement) {
          videoElement.play().catch(err => {
            console.debug('Auto-play prevented:', err);
          });
        }

        // Note: Loom VideoPopover doesn't support auto-play
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [videoUnlocked, gatedVideo]);

  // @todo Podcast embed

  return (
    <>
      <SectionWrapper container={gatedVideo ? "large" : "thin"} padding="none">
        <div className="pt-24 mb-12">
          {seriesVideos.length > 0 ? (
            <div className="text-secodary mb-8">
              <Button
                text="Explore series"
                href={seriesPath}
                icon={<ArrowLeft size="12" />}
                bgColor="bg-dark-10"
              />
            </div>
          ) : (
            <BackToZone to="/resources/" text={site.token("backToResources")} />
          )}

          {gatedVideo ? (
            // GATED: Video above, then two-column layout
            <>
              {videoUnlocked && mainVideo && (
                <div className="mb-12 rounded-xl overflow-hidden" ref={videoContainerRef}>
                  <div className={styles.videoWrapper}>
                    <ExternalVideo key={`gated-${videoUnlocked}`} node={mainVideo} inline={true} />
                  </div>
                </div>
              )}

              <div className="grid lg:grid-cols-2 gap-0 md:gap-24">
                {/* LEFT COLUMN: Title + Body */}
                <div>
                  <h1 className="text-2xl md:text-5xl font-bold tracking-tight mb-4">
                    {title}
                  </h1>
                  <div className={cn("leading-relaxed", postStyles.mainContent)}>
                    {_rawBody && <PortableText blocks={_rawBody} />}
                  </div>
                </div>

                {/* RIGHT COLUMN: Form or success message */}
                <div className="mb-8 md:mt-20 order-first lg:order-last">
                  {!videoUnlocked ? (
                    <HubspotWebform
                      section={{ formId: HUBSPOT_ASSET_FORM_ID }}
                      onFormSubmitted={handleFormSubmit}
                    />
                  ) : (
                    <div className="p-6 bg-dark-5 border border-dark-20 rounded-lg">
                      <h3 className="text-2xl font-semibold mb-2">
                        Video Unlocked
                      </h3>
                      <p className="text-green-700">
                        Thanks. Enjoy the video.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            // NON-GATED: Original layout (uses popover for Vimeo/Loom)
            <>
              <div className="rounded-xl overflow-hidden">
                <div className={styles.videoWrapper}>
                  {mainVideo && <ExternalVideo node={mainVideo} inline={false} />}
                </div>
              </div>
              <h1 className="mt-6 mb-2 text-xl md-text-2xl font-semibold">
                {`${title} [video]`}
              </h1>
              <div className={cn("leading-relaxed", postStyles.mainContent)}>
                {_rawBody && <PortableText blocks={_rawBody} />}
              </div>
            </>
          )}
        </div>
        <Divider />
      </SectionWrapper>

      {seriesVideos.length > 0 ? (
        <SectionWrapper
          layout="centered"
          container="thin"
          header={`${seriesTitle} series`}
        >
          <CardGrid
            maxColumns={2}
            nodes={seriesVideos.slice(0, 4)}
            panels={true}
            hideEvents={true}
            attribution={{ action: "suggestion", label: "video-series" }}
            browseMoreHref={seriesPath}
            browseMoreText="Explore series"
            hideType={true}
            hideDate={true}
            showExtended={true}
          />
        </SectionWrapper>
      ) : (
        <SectionWrapper
          header={site.token("resourcesSuggestions")}
          anchor="more"
          layout="centered"
          container="thin"
        >
          <ResourcesSuggestions exclude={videoId} maxColumns={2} />
        </SectionWrapper>
      )}

      <SectionWrapper container="thin" padding="bottom">
        <ChannelsButtons />
      </SectionWrapper>

      <SectionWrapper container="thin" background="mint" name="getStarted">
        <GetStarted />
      </SectionWrapper>
    </>
  );
};
